import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Modal from '../ui/Modal';
import { Banner } from '../ui/Banner';
import {
  FilterPill, FilterPillGroup, TextInput, TextArea, FieldLabel, FieldHint,
  PrimaryButton, SecondaryButton, LinkButton,
} from '../../ui/common';
import { CameraIcon, XIcon } from '../../ui/icons';
import { useTranslation } from '../../translations';
import { API_BASE } from '../../config';
import { prepareUploadPhoto } from '../../utils/imageThumb';
import {
  KATEGORIE, MAKS_TEMAT, MIN_TRESC, MAKS_TRESC, MAKS_ZRZUTOW, ADRES_ZGLOSZEN,
  sciezkaStrony, rozmiarOkna, dodajZrzuty, sprawdzFormularz, buildMailto, adresZapasowy,
  wczytajSzkic, zapiszSzkic, usunSzkic,
} from '../../utils/supportRequest';

/**
 * H.2 — okno zgłoszenia z przycisku „?”: błąd, pytanie, propozycja. Wysyła na
 * `POST /api/support-request` (H.1, routes/support.js). Firmę, konto i adres odpowiedzi serwer
 * bierze z SESJI; tu pokazujemy je tylko po to, żeby użytkownik wiedział, co wysyła i gdzie
 * odpowiemy (dane pobrane z /api/whoami, czyli z tego samego źródła, co serwer).
 *
 * Zrzuty ekranu są opcjonalne. Nic nie robi zrzutu samo: użytkownik wkleja, przeciąga albo
 * wybiera plik. Obrazy żyją tylko w pamięci okna (zamknięcie je porzuca), a przed wysłaniem
 * przechodzą przez `prepareUploadPhoto` (zmniejszenie). Tekst zostaje w sessionStorage do wysłania.
 *
 * Gdy serwer nie wyśle zgłoszenia (502, brak sieci, limit, chwilowe przeciążenie 503, wygasła
 * sesja), okno daje gotowy `mailto:`, żeby zgłoszenie nie przepadło. `mailto:` nie przenosi
 * załączników, więc mówimy to wprost.
 */

const sesja = () => {
  try { return window.sessionStorage; } catch (_) { return null; }
};

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--sp-3);
  flex-wrap: wrap;
`;

const Counter = styled(FieldHint)`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const ShotsToggle = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;

  svg { width: 18px; height: 18px; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: var(--r-sm); }
`;

const DropZone = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-4);
  border: 2px dashed ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  border-radius: var(--r-md);
  background: ${(p) => (p.$active ? 'var(--hover-bg)' : 'transparent')};
  font-size: var(--fs-sm);
  color: var(--text-muted);
  transition: border-color var(--dur) var(--ease), background var(--dur) var(--ease);
`;

const Thumbs = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Thumb = styled.li`
  position: relative;
  width: 96px;
  height: 72px;
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  overflow: hidden;
  background: var(--card-bg);

  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const ThumbRemove = styled.button.attrs({ type: 'button' })`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;

  svg { width: 14px; height: 14px; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; }
`;

const Context = styled.details`
  font-size: var(--fs-sm);
  color: var(--text-muted);

  summary { cursor: pointer; font-weight: 600; color: var(--text); }
  p { margin: var(--sp-2) 0; }
  dl {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: var(--sp-1) var(--sp-3);
    margin: 0;
  }
  dt { font-weight: 600; }
  dd { margin: 0; overflow-wrap: anywhere; }
`;

const Ticket = styled.div`
  font-size: var(--fs-xl);
  font-weight: 700;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
  color: var(--text);
`;

// Pole pliku schowane WIZUALNIE, nie przez `display: none`: część Safari na iOS i widoków WWW
// ignoruje programowe `click()` na polu z `display: none`, więc „wybierz plik” nic by nie otwierało.
// Ten sam wzorzec co w kreatorze importu (pages/ImportData.js).
const Hidden = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

let licznikZrzutow = 0;

// `wstepne` ({ kategoria, temat, tresc }): okno otwarte z gotową treścią zamiast szkicu, np. „Chcę
// Premium” (components/premium/PremiumUpgrade.js, F.2). Użytkownik może ją zmienić przed wysłaniem.
// Takie okno nie czyta, nie zapisuje i nie kasuje szkicu: szkic należy do zgłoszenia z przycisku
// pomocy, więc prośba o Premium nie może go nadpisać ani skasować po wysłaniu.
// `elevated`: okno nad innymi oknami (warstwa --z-confirm), bo „Chcę Premium” bywa otwierane
// z nakładki szczegółów rezerwacji (--z-modal), pod którą zwykłe okno byłoby niewidoczne.
export default function SupportRequestModal({ open, kategoriaStartowa, onClose, user, wstepne = null, elevated = false }) {
  const { t, currentLanguage } = useTranslation();
  const location = useLocation();
  const idPola = useId();
  const userId = user && user.userId;

  const [kategoria, setKategoria] = useState('bug');
  const [temat, setTemat] = useState('');
  const [tresc, setTresc] = useState('');
  const [zrzuty, setZrzuty] = useState([]); // { id, plik, url }
  const [zrzutyOtwarte, setZrzutyOtwarte] = useState(false);
  const [infoZrzuty, setInfoZrzuty] = useState(null);
  const [przeciaganie, setPrzeciaganie] = useState(false);
  const [blad, setBlad] = useState(null);
  const [stan, setStan] = useState('edycja'); // edycja | wysylanie | wyslane | zapas
  const [powodZapasu, setPowodZapasu] = useState(null);
  const [adresZapasu, setAdresZapasu] = useState(ADRES_ZGLOSZEN);
  const [wynik, setWynik] = useState(null);
  const [konto, setKonto] = useState({ email: '', imie: '', firma: '' });

  const zrzutyRef = useRef([]);
  zrzutyRef.current = zrzuty;
  const inputRef = useRef(null);
  const bladRef = useRef(null);

  // Komunikat błędu stoi na końcu formularza, pod zrzutami i kontekstem, czyli zwykle poza widokiem
  // przewijanego okna. Bez przewinięcia „Wyślij” wyglądałoby na martwy przycisk.
  useEffect(() => {
    if (blad && bladRef.current && bladRef.current.scrollIntoView) {
      bladRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [blad]);

  const zwolnijPodglady = useCallback((lista) => {
    lista.forEach((z) => { try { URL.revokeObjectURL(z.url); } catch (_) { /* brak API w teście */ } });
  }, []);

  // Otwarcie: szkic z sessionStorage, kategoria z menu i dane konta.
  useEffect(() => {
    if (!open) return undefined;
    const szkic = wstepne || wczytajSzkic(sesja(), userId);
    const kat = (wstepne && wstepne.kategoria) || kategoriaStartowa || (szkic && szkic.kategoria) || 'bug';
    setKategoria(kat);
    setTemat((szkic && szkic.temat) || '');
    setTresc((szkic && szkic.tresc) || '');
    setZrzutyOtwarte(kat === 'bug');
    setStan('edycja');
    setBlad(null);
    setInfoZrzuty(null);
    setWynik(null);
    setPowodZapasu(null);
    const zSesji = {
      email: (user && user.email) || '',
      imie: [user && user.firstname, user && user.lastname].filter(Boolean).join(' '),
      firma: (user && user.organizationName) || (() => {
        try { return localStorage.getItem('organizationName') || ''; } catch (_) { return ''; }
      })(),
    };
    setKonto(zSesji);
    // Po odświeżeniu strony `user` pochodzi z /api/whoami i ma wszystko. Po samym logowaniu bywa
    // uboższy (bez e-maila albo firmy) — tylko wtedy pytamy serwer, nie przy każdym otwarciu.
    if (zSesji.email && zSesji.firma) return undefined;

    let aktualne = true;
    fetch(`${API_BASE}/api/whoami`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const u = d && d.user;
        if (!aktualne || !u) return;
        setKonto({
          email: u.email || '',
          imie: [u.firstname, u.lastname].filter(Boolean).join(' '),
          firma: u.organizationName || '',
        });
      })
      .catch(() => { /* zostają dane z sesji panelu */ });
    return () => { aktualne = false; };
    // Dane konta z propsa są tylko wartością startową; nie przeładowujemy okna przy ich zmianie,
    // dlatego `user` celowo nie jest zależnością. `wstepne` jest stałe, dopóki okno jest otwarte.
  }, [open, kategoriaStartowa, userId, wstepne]);

  // Szkic tekstu: zamknięcie okna go nie kasuje, znika dopiero po wysłaniu. Zapis idzie przy
  // ZMIANIE pola, nie w efekcie: efekt biegłby w tym samym przebiegu co wczytanie szkicu, jeszcze
  // ze starym (przy pierwszym otwarciu pustym) stanem, i kasowałby szkic, zanim trafi do stanu.
  const zmien = (zmiana) => {
    const nowy = { kategoria, temat, tresc, ...zmiana };
    if ('kategoria' in zmiana) setKategoria(nowy.kategoria);
    if ('temat' in zmiana) setTemat(nowy.temat);
    if ('tresc' in zmiana) setTresc(nowy.tresc);
    if (!wstepne) zapiszSzkic(sesja(), userId, nowy);
  };

  // Podglądy zrzutów zwalniamy przy odmontowaniu.
  useEffect(() => () => zwolnijPodglady(zrzutyRef.current), [zwolnijPodglady]);

  const dodaj = useCallback((pliki) => {
    const obecne = zrzutyRef.current;
    const { lista, nieObrazy, ponadLimit } = dodajZrzuty(obecne.map((z) => z.plik), pliki);
    const nowe = lista.slice(obecne.length).map((plik) => {
      licznikZrzutow += 1;
      return { id: `z${licznikZrzutow}`, plik, url: URL.createObjectURL(plik) };
    });
    if (nowe.length) setZrzuty([...obecne, ...nowe]);
    setZrzutyOtwarte(true);
    if (ponadLimit) setInfoZrzuty(t('help.shots.limit', { max: MAKS_ZRZUTOW }));
    else if (nieObrazy) setInfoZrzuty(t('help.shots.notImage'));
    else setInfoZrzuty(null);
  }, [t]);

  const usun = (id) => {
    const z = zrzutyRef.current.find((x) => x.id === id);
    if (z) zwolnijPodglady([z]);
    setZrzuty(zrzutyRef.current.filter((x) => x.id !== id));
    setInfoZrzuty(null);
  };

  // Wklejenie (Ctrl+V) w dowolnym miejscu okna. Zwykły tekst wkleja się jak zawsze — przejmujemy
  // zdarzenie tylko wtedy, gdy schowek niesie obraz.
  useEffect(() => {
    if (!open || stan !== 'edycja') return undefined;
    const naWklejenie = (e) => {
      const pliki = Array.from((e.clipboardData && e.clipboardData.files) || []);
      if (!pliki.some((p) => /^image\//.test(p.type || ''))) return;
      e.preventDefault();
      dodaj(pliki);
    };
    document.addEventListener('paste', naWklejenie);
    return () => document.removeEventListener('paste', naWklejenie);
  }, [open, stan, dodaj]);

  const zamknij = () => {
    zwolnijPodglady(zrzutyRef.current);
    setZrzuty([]);
    onClose();
  };

  // Klucze wypisane wprost, nie składane z kodu: audyt tłumaczeń widzi tylko stałe `t('...')`,
  // a klucz, którego „nikt nie woła”, ląduje na liście martwych i ktoś go w końcu skasuje.
  const etykietaKategorii = {
    bug: t('help.category.bug'),
    question: t('help.category.question'),
    idea: t('help.category.idea'),
    other: t('help.category.other'),
  };
  const podpowiedz = {
    bug: t('help.form.hint.bug'),
    question: t('help.form.hint.question'),
    idea: t('help.form.hint.idea'),
    other: t('help.form.hint.other'),
  };
  // Kody te same co w odpowiedziach serwera (routes/support.js) i w utils/supportRequest.js.
  const komunikat = (kod) => ({
    invalid_category: t('help.error.invalid_category'),
    subject_required: t('help.error.subject_required'),
    subject_too_long: t('help.error.subject_too_long', { max: MAKS_TEMAT }),
    message_too_short: t('help.error.message_too_short', { min: MIN_TRESC }),
    message_too_long: t('help.error.message_too_long', { max: MAKS_TRESC }),
    too_many_attachments: t('help.error.too_many_attachments', { max: MAKS_ZRZUTOW }),
    attachment_too_large: t('help.error.attachment_too_large'),
    attachment_not_image: t('help.error.attachment_not_image'),
    attachment_unreadable: t('help.error.attachment_unreadable'),
    request_too_large: t('help.error.request_too_large'),
  }[kod] || t('help.error.generic'));

  const strona = sciezkaStrony(location.pathname);
  const okno = typeof window !== 'undefined' ? rozmiarOkna(window) : '';
  const przegladarka = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const kontoOpis = [konto.imie, konto.email && `(${konto.email})`].filter(Boolean).join(' ');

  // Dokładnie to, co serwer dopisuje do maila (routes/support.js), w języku panelu.
  const kontekst = [
    [t('help.context.page'), strona],
    [t('help.context.browser'), przegladarka],
    [t('help.context.language'), currentLanguage],
    [t('help.context.window'), okno],
    [t('help.context.org'), konto.firma],
    [t('help.context.account'), kontoOpis],
    [t('help.context.role'), t('help.context.roleValue')],
    [t('help.context.attachments'), zrzuty.length ? String(zrzuty.length) : t('help.context.none')],
  ];

  const wyslij = async () => {
    const kod = sprawdzFormularz({ kategoria, temat, tresc });
    if (kod) { setBlad(komunikat(kod)); return; }
    setBlad(null);
    setStan('wysylanie');

    let odp;
    try {
      const dane = new FormData();
      dane.append('category', kategoria);
      dane.append('subject', temat.trim());
      dane.append('message', tresc.trim());
      dane.append('page', strona);
      dane.append('lang', currentLanguage || '');
      dane.append('viewport', okno);
      // Zmniejszanie zrzutów jest niezależne, więc równolegle; kolejność w FormData zostaje.
      const gotowe = await Promise.all(zrzutyRef.current.map((z) => prepareUploadPhoto(z.plik)));
      gotowe.forEach((plik, i) => dane.append('attachments', plik, plik.name || `zrzut-${i + 1}.png`));
      odp = await fetch(`${API_BASE}/api/support-request`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': localStorage.getItem('csrfToken') || '' },
        body: dane,
      });
    } catch (_) {
      setPowodZapasu(null);
      setAdresZapasu(ADRES_ZGLOSZEN);
      setStan('zapas');
      return;
    }

    let cialo = {};
    try { cialo = await odp.json(); } catch (_) { /* odpowiedź bez JSON-a */ }
    setAdresZapasu(adresZapasowy(cialo.supportEmail));

    if (odp.status === 201) {
      if (!wstepne) usunSzkic(sesja(), userId);
      zwolnijPodglady(zrzutyRef.current);
      setZrzuty([]);
      setWynik({ ticket: cialo.ticket, replyTo: cialo.replyTo === undefined ? konto.email : cialo.replyTo });
      setStan('wyslane');
      return;
    }
    if (odp.status === 400 || odp.status === 413) {
      // 413 z bramki S.16 w server.js (za duże ciało, zanim trasa ruszy) nie niesie kodu `error`,
      // tylko `message` — to wciąż „zrzuty są łącznie za duże”, nie błąd ogólny.
      setBlad(komunikat(cialo.error || (odp.status === 413 ? 'request_too_large' : undefined)));
      setStan('edycja');
      return;
    }
    if (odp.status === 429) {
      const minuty = Math.max(1, Math.ceil((Number(cialo.retryAfter) || 60) / 60));
      setPowodZapasu(t('help.error.too_many_requests', { minutes: minuty }));
    } else if (odp.status === 503) {
      // Serwer chwilowo nie ma miejsca na treść żądań (`server_busy`, routes/support.js). Zgłoszenie
      // jest w porządku, więc jak przy 429: kiedy ponowić, i mailto. Nie „zrzuty za duże”.
      const sekundy = Math.max(1, Math.round(Number(cialo.retryAfter) || 5));
      setPowodZapasu(t('help.error.server_busy', { seconds: sekundy }));
    } else if (odp.status === 401 || odp.status === 403) {
      setPowodZapasu(t('help.error.session'));
    } else {
      setPowodZapasu(null);
    }
    setStan('zapas');
  };

  const mailto = stan === 'zapas' ? buildMailto({
    to: adresZapasu,
    kategoria,
    temat,
    tresc,
    kontekst,
    dopisek: zrzuty.length ? t('help.mailto.attachNote', { n: zrzuty.length }) : '',
  }) : '';

  const wysylanie = stan === 'wysylanie';

  let stopka;
  if (stan === 'wyslane') {
    stopka = <PrimaryButton type="button" onClick={zamknij} data-cy="help-done-close">{t('help.form.close')}</PrimaryButton>;
  } else if (stan === 'zapas') {
    stopka = (
      <>
        <SecondaryButton type="button" onClick={zamknij}>{t('help.form.close')}</SecondaryButton>
        <PrimaryButton type="button" onClick={() => setStan('edycja')} data-cy="help-retry">{t('help.fallback.retry')}</PrimaryButton>
      </>
    );
  } else {
    stopka = (
      <>
        <SecondaryButton type="button" onClick={zamknij} disabled={wysylanie}>{t('help.form.cancel')}</SecondaryButton>
        <PrimaryButton type="button" onClick={wyslij} disabled={wysylanie} data-cy="help-send">
          {wysylanie ? t('help.form.sending') : t('help.form.send')}
        </PrimaryButton>
      </>
    );
  }

  return (
    <Modal
      open={open}
      elevated={elevated}
      onClose={wysylanie ? () => {} : zamknij}
      title={t('help.form.title')}
      subtitle={t('help.form.subtitle')}
      closeLabel={t('help.form.close')}
      footer={stopka}
      data-cy="help-modal"
    >
      {stan === 'wyslane' && wynik ? (
        <Form data-cy="help-done">
          <Banner tone="success" title={t('help.done.title')}>
            <div>{t('help.done.ticket')}</div>
            <Ticket data-cy="help-ticket">{wynik.ticket}</Ticket>
          </Banner>
          <div data-cy="help-done-reply">
            {wynik.replyTo ? t('help.done.reply', { email: wynik.replyTo }) : t('help.done.replyNoEmail')}
          </div>
        </Form>
      ) : stan === 'zapas' ? (
        <Form data-cy="help-fallback">
          <Banner tone="warning" title={t('help.fallback.title')}>
            {powodZapasu ? <div data-cy="help-fallback-reason">{powodZapasu}</div> : null}
            <div>{t('help.fallback.body')}</div>
          </Banner>
          <a href={mailto} data-cy="help-mailto">{t('help.fallback.link')}</a>
          <div>{t('help.fallback.address', { email: adresZapasu })}</div>
          {zrzuty.length ? (
            <Banner tone="info" compact data-cy="help-fallback-attach-note">{t('help.fallback.attachNote')}</Banner>
          ) : null}
        </Form>
      ) : (
        <Form>
          <div>
            <FieldLabel as="div" id={`${idPola}-kat`}>{t('help.form.category')}</FieldLabel>
            <FilterPillGroup role="group" aria-labelledby={`${idPola}-kat`}>
              {KATEGORIE.map((k) => (
                <FilterPill
                  key={k}
                  $active={kategoria === k}
                  onClick={() => zmien({ kategoria: k })}
                  disabled={wysylanie}
                  data-cy={`help-category-${k}`}
                >
                  {etykietaKategorii[k]}
                </FilterPill>
              ))}
            </FilterPillGroup>
          </div>

          <div>
            <FieldLabel htmlFor={`${idPola}-temat`}>{t('help.form.subject')}</FieldLabel>
            <TextInput
              id={`${idPola}-temat`}
              value={temat}
              onChange={(e) => zmien({ temat: e.target.value })}
              maxLength={MAKS_TEMAT}
              disabled={wysylanie}
              data-cy="help-subject"
            />
          </div>

          <div>
            <FieldLabel htmlFor={`${idPola}-opis`}>{t('help.form.message')}</FieldLabel>
            <TextArea
              id={`${idPola}-opis`}
              value={tresc}
              onChange={(e) => zmien({ tresc: e.target.value })}
              placeholder={podpowiedz[kategoria]}
              maxLength={MAKS_TRESC}
              rows={5}
              disabled={wysylanie}
              data-cy="help-message"
            />
            <Row>
              <FieldHint>{t('help.form.noCustomerData')}</FieldHint>
              <Counter>{t('help.form.counter', { n: tresc.length, max: MAKS_TRESC })}</Counter>
            </Row>
          </div>

          <ShotsToggle
            onClick={() => setZrzutyOtwarte((v) => !v)}
            aria-expanded={zrzutyOtwarte}
            data-cy="help-shots-toggle"
          >
            <CameraIcon />
            {t('help.shots.toggle')}
          </ShotsToggle>

          {zrzutyOtwarte ? (
            <DropZone
              $active={przeciaganie}
              onDragOver={(e) => { e.preventDefault(); setPrzeciaganie(true); }}
              onDragLeave={() => setPrzeciaganie(false)}
              onDrop={(e) => {
                e.preventDefault();
                setPrzeciaganie(false);
                dodaj(e.dataTransfer && e.dataTransfer.files);
              }}
              data-cy="help-shots-drop"
            >
              <div>
                {t('help.shots.drop')}{' '}
                <LinkButton onClick={() => inputRef.current && inputRef.current.click()} disabled={wysylanie}>
                  {t('help.shots.choose')}
                </LinkButton>
                <Hidden
                  ref={inputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/*"
                  multiple
                  onChange={(e) => { dodaj(e.target.files); e.target.value = ''; }}
                  data-cy="help-shots-input"
                />
              </div>
              {zrzuty.length ? (
                <Thumbs>
                  {zrzuty.map((z, i) => (
                    <Thumb key={z.id} data-cy="help-shot">
                      <img src={z.url} alt={t('help.shots.alt', { n: i + 1 })} />
                      <ThumbRemove
                        onClick={() => usun(z.id)}
                        aria-label={t('help.shots.remove', { n: i + 1 })}
                        disabled={wysylanie}
                        data-cy="help-shot-remove"
                      >
                        <XIcon />
                      </ThumbRemove>
                    </Thumb>
                  ))}
                </Thumbs>
              ) : null}
              <div data-cy="help-shots-count">{t('help.shots.count', { n: zrzuty.length, max: MAKS_ZRZUTOW })}</div>
              {infoZrzuty ? <Banner tone="info" compact data-cy="help-shots-info">{infoZrzuty}</Banner> : null}
              <Banner tone="warning" compact data-cy="help-shots-warning">{t('help.shots.warning')}</Banner>
              <FieldHint>{t('help.shots.howTo')}</FieldHint>
            </DropZone>
          ) : null}

          <div>
            <FieldLabel htmlFor={`${idPola}-odp`}>{t('help.form.replyTo')}</FieldLabel>
            {konto.email ? (
              <TextInput id={`${idPola}-odp`} value={konto.email} readOnly data-cy="help-reply-to" />
            ) : (
              <Banner tone="warning" compact data-cy="help-reply-missing">{t('help.form.replyToMissing')}</Banner>
            )}
          </div>

          <Context data-cy="help-context">
            <summary>{t('help.context.summary')}</summary>
            <p>{t('help.context.intro')}</p>
            <dl>
              {kontekst.map(([etykieta, wartosc]) => (
                <React.Fragment key={etykieta}>
                  <dt>{etykieta}</dt>
                  <dd>{wartosc || t('help.context.none')}</dd>
                </React.Fragment>
              ))}
            </dl>
          </Context>

          {blad ? (
            <div ref={bladRef}>
              <Banner tone="danger" compact data-cy="help-error">{blad}</Banner>
            </div>
          ) : null}
        </Form>
      )}
    </Modal>
  );
}
